import { FastField }                                                                    from 'formik';
import { ComponentType, FC, ReactElement, useCallback, useEffect, useRef, useState }  from 'react';
import styled                                                                           from 'styled-components';
import { CreateCategory }                                                               from '../components/documents/CreateCategory';
import { AlertDialog }                                                                  from '../components/modals/AlertDialog';
import { SelectField }                                                                  from '../formFields/SelectField';
import { ICategory, ISubCategory, useDeleteDocumentSubCategory, useDeleteDocumentType } from '../queries/categories';
import DeleteIcon from '@mui/icons-material/Delete';

const DeleteEventContent = styled.div`
  line-height: 28px;
`;

const AddCategorySpacing = styled.div`
  margin: 10px;
`;

const StyledName = styled.span<{ isPinned: boolean }>`
  font-weight: 'bold';
`;

const TrashIcon = styled(DeleteIcon)`&& {
  color: #1f1e1e;
  width: 20px;
  background: none;
}`;

const EditButton = styled.button`
  margin-left: 10px;
  padding: 5px;
  background-color: transparent;
  border: none;
  border-radius: 3px;
  cursor: pointer;
`;

export interface ISettingsPageWithAvatarComponent {
  categoriesDropdown: ReactElement;
  subCategoriesDropdown: ReactElement;
}

export interface IWithCategorySelectionProps {
  familyId?: string;
  values: any;
  categories?: ICategory[];
  loading: boolean;

  setFieldValue(field: string, value: any, shouldValidate?: boolean): void;
}

const CATEGORIES = [
  'Finance',
  'General',
  'Legal',
  'Medical',
  'School',
];

const GENERAL = [
  'Airline Clubs',
  'Concerns',
  'Grocery',
  'ID',
  'Loyalty Clubs',
  'Parties',
  'Schedule',
  'Sports',
  'Text Responses',
  'Travel',
  'Vacations',
  'General',
];

const FINANCE = [
  'Banking Statements',
  'Credit Card Documents',
  'General',
];

const LEGAL = [
  'Adoption',
  'Auto Insurance',
  'Auto Registration',
  'Birth Certificate',
  'Custody Documents',
  "Driver's License",
  'Legal Fees',
  'Legal Fee',
  'Life Insurance',
  'Marriage Certificate',
  'Passport',
  'Power of Attorney',
  'Power Of Attorney',
  'SSN',
  'General',
];

const MEDICAL = [
  'Dental',
  'Dental Insurance Card',
  'Medical Insurance Card',
  'Prescriptions',
  'Reports',
  'Vaccinations',
  'Vision',
  'General',
];

const SCHOOL = [
  'School',
  'Art',
  'Grade Reports',
  'Permission Slips',
  'Resume',
  'School ID',
  'General',
];

export const WithCategorySelection = (
  Component: ComponentType<ISettingsPageWithAvatarComponent>
): FC<IWithCategorySelectionProps> => function WithCategorySelectionInner({
    values,
    setFieldValue,
    familyId,
    categories,
    loading,
  }) {
    // Refs are now correctly inside the function component (not in the HOC factory)
    const addCategoryMenuAnchorRef = useRef(null);
    const addSubCategoryMenuAnchorRef = useRef(null);
    const pendingCategorySync = useRef(false);
    const pendingSubCategorySync = useRef(false);

    const { mutate: deleteDocumentCategory } = useDeleteDocumentType();
    const { mutate: deleteSubDocumentCategory } = useDeleteDocumentSubCategory();

    const [ categoriesList, setCategoriesList ] = useState([] as ICategory[] | any);
    const [ subCategoriesList, setSubCategoriesList ] = useState([] as ISubCategory[] | any);
    const [ selectedCategories, setSelectedCategories ] = useState({} as ICategory | null);
    const [ selectedSubCategories, setSelectedSubCategories ] = useState({} as ISubCategory | null);

    const [ deleteCategoryModalOpen, setDeleteCategoryModalOpen ] = useState(false);
    const [ deleteSubCategoryModalOpen, setDeleteSubCategoryModalOpen ] = useState(false);

    const sortCategories = (categories: any, pinnedNames: any) => {
      const sortByLocal = (a: any, b: any) => {
        if (a.isBasic === b.isBasic) {
          return a.name.localeCompare(b.name);
        }
        return a.isBasic ? -1 : 1;
      };

      const pinned = categories
        .filter((category: ICategory) => pinnedNames.includes(category.name))
        .sort(sortByLocal);

      const others = categories
        .filter((category: ICategory) => !pinnedNames.includes(category.name))
        .sort(sortByLocal);

      return [...pinned, ...others];
    };

    const sortSubCategories = (subCategories: ISubCategory[], pinnedNames: string[]) => {
      const sortByLocal = (a: ISubCategory, b: ISubCategory) => {
        if (a.isBasic === b.isBasic) {
          return a.name.localeCompare(b.name);
        }
        return a.isBasic ? -1 : 1;
      };

      const pinned = subCategories
        .filter((item: ISubCategory) => pinnedNames.includes(item.name))
        .sort(sortByLocal);

      const others = subCategories
        .filter((item: ISubCategory) => !pinnedNames.includes(item.name))
        .sort(sortByLocal);

      return [...pinned, ...others];
    };

    const handleEditCategory = (category: ICategory) => {
      setSelectedCategories(category);
      setDeleteCategoryModalOpen(true);
    };

    const handleEditSubCategory = (category: ISubCategory) => {
      setSelectedSubCategories(category);
      setDeleteSubCategoryModalOpen(true);
    };

    const onDeleteCategory = () => {
      if(familyId && selectedCategories) {
        deleteDocumentCategory({
          categoryId: selectedCategories.id,
          familyId,
        }, {
          onSuccess: () => {
            const generalId = categoriesList.find(({ name }: any) => name === 'General')?.id || '';
            setCategoriesList((prev: ICategory[]) => prev.filter(c => c.id !== selectedCategories.id));
            setFieldValue('categoryId', generalId);
            setDeleteCategoryModalOpen(false);
            setSelectedCategories(null);
          },
        });
      }
    };

    const onDeleteSubCategory = () => {
      if(familyId && selectedSubCategories) {
        deleteSubDocumentCategory({
          subСategoryId: selectedSubCategories.id,
          familyId,
        }, {
          onSuccess: () => {
            const generalId = subCategoriesList.find(({ name }: ISubCategory) => name === 'General')?.id || '';
            setSubCategoriesList((prev: ISubCategory[]) => prev.filter(s => s.id !== selectedSubCategories.id));
            setFieldValue('subCategoryId', generalId);
            setDeleteSubCategoryModalOpen(false);
            setSelectedSubCategories(null);
          },
        });
      }
    };

    useEffect(() => {
      if (!categories) return;

      if (!categoriesList.length) {
        const list = categories.map(({ id, name, isBasic }: ICategory) => ({ id, name, isBasic }));
        setCategoriesList(sortCategories(list, [...CATEGORIES]) as ICategory[]);

        const category = values?.categoryId
          ? values?.categoryId
          : list.find(({ name }) => name === 'General')?.id || '';

        setFieldValue('categoryId', category);
      } else if (pendingCategorySync.current) {
        pendingCategorySync.current = false;
        const currentIds = new Set(categoriesList.map((c: ICategory) => c.id));
        const newCategory = categories.find(({ id }) => !currentIds.has(id));
        const list = categories.map(({ id, name, isBasic }: ICategory) => ({ id, name, isBasic }));
        setCategoriesList(sortCategories(list, [...CATEGORIES]) as ICategory[]);
        if (newCategory) {
          setFieldValue('categoryId', newCategory.id);
        }
      }
    }, [categories, loading]);

    const pinnedNames = [
      ...GENERAL,
      ...FINANCE,
      ...LEGAL,
      ...MEDICAL,
      ...SCHOOL,
    ];

    useEffect(() => {
      const categoryId = values?.categoryId;

      // Wait for categories to load — syncing against an empty list would wipe
      // the saved subCategoryId before it can be matched (e.g. on document edit)
      if (!categories?.length) return;

      if (categoryId && Object.hasOwnProperty.call(values, 'subCategoryId')) {
        const freshSubCategories = sortSubCategories(
          categories?.find(type => type.id === categoryId)
            ?.subCategories?.map(({ id, name, isBasic }) => ({ id, name, isBasic })) || [],
          [ ...GENERAL, ...FINANCE, ...LEGAL, ...MEDICAL, ...SCHOOL, ...pinnedNames ],
        );

        setSubCategoriesList(freshSubCategories as ISubCategory[]);

        let subCategory: string;
        if (pendingSubCategorySync.current) {
          pendingSubCategorySync.current = false;
          // subCategoriesList (state, not shadowed) holds the pre-refetch list
          const previousIds = new Set(subCategoriesList.map((s: ISubCategory) => s.id));
          const newSub = freshSubCategories.find(({ id }) => !previousIds.has(id));
          subCategory = newSub?.id
            || freshSubCategories.find(({ name }: ISubCategory) => name === 'General')?.id
            || '';
        } else {
          const isSubCategoryInCurrentList = freshSubCategories.find(({ id }) => id === values?.subCategoryId);
          subCategory = values?.subCategoryId !== undefined && isSubCategoryInCurrentList
            ? values?.subCategoryId
            : freshSubCategories.find(({ name }) => name === 'General')?.id || '';
        }

        setFieldValue('subCategoryId', subCategory);
      } else if (categoryId && !values?.subCategoryId) {
        const defaultSubCategory = categories?.find(type => type.id === categoryId)
          ?.subCategories?.find(({ name }) => name === 'General')?.id || '';
        setFieldValue('subCategoryId', defaultSubCategory);
      }
    }, [values?.categoryId, categories, loading]);

    const onCategoryAdded = (data: ICategory) => {
      if (!data?.id) {
        // API didn't return the new category — sync from the next query refetch
        pendingCategorySync.current = true;
        return;
      }
      const updatedList = [...(categories || []), data]
        .map(({ id, name, isBasic }) => ({ id, name, isBasic }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setCategoriesList(updatedList as ICategory[]);
      setFieldValue('categoryId', data.id);
    };

    const onSubCategoryAdded = (data: ISubCategory) => {
      if (!data?.id) {
        pendingSubCategorySync.current = true;
        return;
      }
      const updatedList = [...subCategoriesList, data]
        .map(({ id, name }) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setSubCategoriesList(updatedList);
      setFieldValue('subCategoryId', data.id);
    };

    const categoriesOptions = useCallback(() => [
      ...categoriesList.map((item: ICategory) => ({
        value : item.id,
        label : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <StyledName isPinned={false}>{item.name}</StyledName>
            {!item.isBasic && <EditButton onClick={(event) => {
              event.stopPropagation();
              handleEditCategory(item);
            }}
                              >
              <TrashIcon />
            </EditButton>}
          </div>
        ),
      })),
      (
        <AddCategorySpacing key={familyId}> {/* eslint-disable  react/jsx-key*/}
          <CreateCategory
            outerRef={addCategoryMenuAnchorRef}
            familyId={familyId}
            categoryId={values?.categoryId}
            onSuccess={onCategoryAdded}
          />
        </AddCategorySpacing>
      ) ], [ categoriesList ]);

    const subCategoriesOptions = useCallback(() => [
      ...subCategoriesList.map((item: ISubCategory) => ({
        value : item.id,
        label : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <StyledName isPinned={false}>{item.name}</StyledName>
            {!item.isBasic && <EditButton
              style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between' }}
              onClick={(event) => {
                event.stopPropagation();
                handleEditSubCategory(item);
              }}
                              >
              <TrashIcon />
            </EditButton>}
          </div>
        ),
      })),
      (
        <AddCategorySpacing> {/* eslint-disable  react/jsx-key*/}
          <CreateCategory
            isSubCategory
            outerRef={addSubCategoryMenuAnchorRef}
            familyId={familyId}
            categoryId={values?.categoryId}
            onSuccess={onSubCategoryAdded}
          />
        </AddCategorySpacing>
      ),
    ], [subCategoriesList]);


    const categoriesField = (
      <div ref={addCategoryMenuAnchorRef}>
        <FastField
          key={categoriesOptions()?.length}
          name={'categoryId'}
          options={categoriesOptions()}
          component={SelectField}
        />

        <AlertDialog
          dialogWidth="350px"
          open={deleteCategoryModalOpen}
          title="Are you sure you want to delete?"
          content={
            <DeleteEventContent>
              If you have files in this <b>{selectedCategories?.name}</b> folder, they will be moved to General folder. Are you sure?
            </DeleteEventContent>
          }
          loading={loading}
          handleSubmit={() => onDeleteCategory()}
          handleCancel={() => {
            setDeleteCategoryModalOpen(false);
            setSelectedCategories(null);
          }}
        />
      </div>
    );

    const subCategoriesField = (
      <div ref={addSubCategoryMenuAnchorRef}>
        <FastField
          key={subCategoriesList.length}
          name={'subCategoryId'}
          options={subCategoriesOptions()}
          component={SelectField}
        />

        <AlertDialog
          dialogWidth="350px"
          open={deleteSubCategoryModalOpen}
          title="Are you sure you want to delete?"
          content={
            <DeleteEventContent>
              If you have files in this <b>{selectedSubCategories?.name}</b> folder, they will be moved to General folder. Are you sure?
            </DeleteEventContent>
          }
          loading={loading}
          handleSubmit={() => onDeleteSubCategory()}
          handleCancel={() => {
            setDeleteSubCategoryModalOpen(false);
            setSelectedSubCategories(null);
          }}
        />
      </div>
    );

    return (
      <Component
        categoriesDropdown={categoriesField}
        subCategoriesDropdown={subCategoriesField}
      />
    );
  };
